// Web-specific Firebase OAuth configuration
// Bypasses cross-platform manager to avoid configuration conflicts

import { initializeApp, FirebaseApp, getApps, getApp } from 'firebase/app';
import { getAuth, Auth, GoogleAuthProvider, OAuthProvider, initializeAuth, indexedDBLocalPersistence, browserLocalPersistence } from 'firebase/auth';
import { firebasePublicConfig } from '../config/firebasePublicConfig';
import { isNative } from '../capacitor-init';

let webFirebaseApp: FirebaseApp | null = null;
let webAuth: Auth | null = null;

const webFirebaseConfig = firebasePublicConfig;

export const initializeWebAuth = (): { app: FirebaseApp; auth: Auth } => {
  console.log('🔥 WebAuth: Initializing web-specific Firebase for OAuth...');
  
  // Check if we already have a web auth instance
  if (webFirebaseApp && webAuth) {
    console.log('🔥 WebAuth: Reusing existing web Firebase app for OAuth');
    return { app: webFirebaseApp, auth: webAuth };
  }
  
  // Try to get existing DEFAULT app first
  const existingApps = getApps();
  console.log('🔍 WebAuth: Existing Firebase apps:', existingApps.map(app => app.name));
  
  if (existingApps.length > 0) {
    try {
      webFirebaseApp = getApp(); // Get DEFAULT app
      console.log('🔥 WebAuth: Using existing DEFAULT Firebase app');
    } catch (error) {
      // No DEFAULT app exists, create one
      webFirebaseApp = initializeApp(webFirebaseConfig);
      console.log('🔥 WebAuth: Created new DEFAULT Firebase app');
    }
  } else {
    // No apps exist, create the first one
    webFirebaseApp = initializeApp(webFirebaseConfig);
    console.log('🔥 WebAuth: Created first Firebase app');
  }
  
  // Initialize auth with the app - Use initializeAuth with indexedDB for Capacitor to prevent GAPI iframe errors
  if (isNative()) {
    console.log('🔥 WebAuth: Native platform detected - using initializeAuth with indexedDB persistence');
    try {
      webAuth = initializeAuth(webFirebaseApp, {
        persistence: indexedDBLocalPersistence
      });
    } catch (error) {
      console.warn('🔥 WebAuth: initializeAuth failed (likely already initialized), falling back to getAuth');
      webAuth = getAuth(webFirebaseApp);
    }
  } else {
    webAuth = getAuth(webFirebaseApp);
  }
  
  console.log('🔥 WebAuth: Web Firebase initialized for OAuth', {
    appName: webFirebaseApp.name,
    projectId: webFirebaseApp.options.projectId,
    authDomain: webFirebaseApp.options.authDomain
  });
  
  return { app: webFirebaseApp, auth: webAuth };
};

export const getWebGoogleProvider = (): GoogleAuthProvider => {
  const provider = new GoogleAuthProvider();
  
  // Configure provider with minimal scopes
  provider.addScope('email');
  provider.addScope('profile');
  
  // Set custom parameters
  provider.setCustomParameters({
    prompt: 'select_account'
  });
  
  console.log('🔧 WebAuth: Google provider configured', {
    providerId: provider.providerId,
    scopes: ['email', 'profile']
  });
  
  return provider;
};

export const getWebAuth = (): Auth => {
  if (!webAuth) {
    const { auth } = initializeWebAuth();
    return auth;
  }
  return webAuth;
};

export const getWebAppleProvider = (): OAuthProvider => {
  const provider = new OAuthProvider('apple.com');

  // Configure provider with scopes
  provider.addScope('email');
  provider.addScope('name');

  // Set custom parameters for Apple Sign-In
  provider.setCustomParameters({
    // Request user's name on first sign-in
    locale: 'en'
  });

  console.log('🍎 WebAuth: Apple provider configured', {
    providerId: provider.providerId,
    scopes: ['email', 'name']
  });

  return provider;
};